import React, { useState } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { FiDownload, FiX, FiFileText, FiBookOpen } from 'react-icons/fi';
import CareerTimelineDynamic from '../components/CareerTimelineDynamic';

// MotionDiv
type MotionDivProps = HTMLMotionProps<'div'> & {
  className?: string;
};

const MotionDiv = motion.div as React.FC<MotionDivProps>;

const About: React.FC = () => {
  const { t } = useLanguage();
  const [showPdf, setShowPdf] = useState(false);
  const [showFullCv, setShowFullCv] = useState(false);

  // PDF Viewer
  const togglePdfViewer = () => {
    setShowPdf(!showPdf);
  };

  // CV Viewer
  const toggleCvView = () => {
    setShowFullCv(!showFullCv);
  };

  const skills = {
    backEnd: [
      'PHP',
      'Symfony',
      'Laravel',
      'Smarty',
      'Java',
      'Node.js',
    ],
    frontEnd: [
      'JavaScript',
      'TypeScript',
      'React.js',
      'Redux',
      'HTML & CSS3',
      'Tailwind CSS',
      'Semantic UI',
    ],
    tools: [
      'GitLab CI/CD',
      'PostgreSQL',
      'MySQL',
      'MongoDB',
      'Oracle'
    ],
    other: [
      'Progressive Web Apps',
      'Responsive Design',
      'RESTful APIs',
      'Shopware-Plugins',
      'WordPress-Plugins'
    ],
  };

  // Download CV
  const handleDownloadCV = () => {
    // Create a temporary link element
    const link = document.createElement('a');
    link.href = '/Lebenslauf.pdf';
    link.download = 'Matthias_Bauer_Lebenslauf.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="py-12">
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto"
      >
        <div className="flex flex-col md:flex-row justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-indigo-600 dark:text-indigo-400">
            {t('about.title')}
          </h1>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
            {t('about.intro')}
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
            {t('about.specialization')}
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
            {t('about.hobbies')}
          </p>
          
          <div className="mt-6">
            <div className="flex flex-wrap gap-4 mt-6 justify-center">
              {!showFullCv && (
                <button
                  onClick={toggleCvView}
                  className="flex items-center px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300"
                >
                  <FiFileText className="mr-2" />
                  {t('about.viewFullCv')}
                </button>
              )}
              <button
                onClick={togglePdfViewer}
                className="hidden md:flex items-center px-6 py-2 border border-indigo-600 text-indigo-600 dark:text-indigo-300 dark:border-indigo-300 rounded-lg hover:bg-indigo-50 dark:hover:bg-gray-700 transition-colors duration-300"
              >
                <FiBookOpen className="mr-2" />
                {t('about.showCv')}
              </button>

              <button
                onClick={handleDownloadCV}
                className="flex items-center px-6 py-2 border border-indigo-600 text-indigo-600 dark:text-indigo-300 dark:border-indigo-300 rounded-lg hover:bg-indigo-50 dark:hover:bg-gray-700 transition-colors duration-300"
              >
                <FiDownload className="mr-2" />
                {t('about.downloadCv')}
              </button>
            </div>
          </div>

          {showPdf && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={togglePdfViewer}>
              <div className="relative w-full max-w-4xl h-[90vh] bg-white rounded-lg overflow-hidden" onClick={e => e.stopPropagation()}>
                <button
                  onClick={togglePdfViewer}
                  className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 z-10 hover:bg-opacity-75 transition-colors"
                  aria-label="Close PDF viewer"
                >
                  <FiX size={24} />
                </button>
                <iframe
                  src="/Lebenslauf.pdf#toolbar=1&view=FitH"
                  className="w-full h-full border-0"
                  title="CV"
                />
              </div>
            </div>
          )}
        </div>


        {showFullCv && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
            <div className="flex justify-end mb-4">
              <button
                onClick={toggleCvView}
                className="flex items-center px-6 py-2 border border-indigo-600 text-indigo-600 dark:text-indigo-300 dark:border-indigo-300 rounded-lg hover:bg-indigo-50 dark:hover:bg-gray-700 transition-colors duration-300"
              >
                <FiX className="mr-2" />
                {t('about.hideFullCv')}
              </button>
            </div>
            <CareerTimelineDynamic />
          </div>
        )}

        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-semibold mb-4 text-indigo-600 dark:text-indigo-400">
            {t('about.skills')}
          </h2>
          
          <h3 className="text-xl font-semibold mb-3 text-indigo-600 dark:text-indigo-400">
            {t('about.skills.backEnd')}
          </h3>
          <div className="flex flex-wrap gap-2" style={{ marginBottom: '2rem' }}>
            {skills.backEnd.map((skill, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 rounded-full text-sm font-medium"
              >
                {skill}
              </span>
            ))}
          </div>
            
          <h3 className="text-xl font-semibold mb-3 text-indigo-600 dark:text-indigo-400">
            {t('about.skills.frontEnd')}
          </h3>
          <div className="flex flex-wrap gap-2" style={{ marginBottom: '2rem' }}>
            {skills.frontEnd.map((skill, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 rounded-full text-sm font-medium"
              >
                {skill}
              </span>
            ))}
          </div>
            
          <h3 className="text-xl font-semibold mb-3 text-indigo-600 dark:text-indigo-400">
            {t('about.skills.tools')}
          </h3>
          <div className="flex flex-wrap gap-2" style={{ marginBottom: '2rem' }}>
            {skills.tools.map((skill, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 rounded-full text-sm font-medium"
              >
                {skill}
              </span>
            ))}
          </div>
            
          <h3 className="text-xl font-semibold mb-3 text-indigo-600 dark:text-indigo-400">
            {t('about.skills.other')}
          </h3>
          <div className="flex flex-wrap gap-2" style={{ marginBottom: '2rem' }}>
            {skills.other.map((skill, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 rounded-full text-sm font-medium"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      </MotionDiv>
    </div>
  );
};

export default About;
